/**
 * Monthly Board History — shared types and URL helpers.
 *
 * History pages are permanent, immutable records of how a board finished in a
 * given calendar month. URLs always use English month slugs so the structure
 * stays stable across the 8 locales; only the rendered labels are localized.
 */
import { LOCALE_META, type Locale } from "@/i18n/locales";

export const MONTH_SLUGS = [
  "january",
  "february",
  "march",
  "april",
  "may",
  "june",
  "july",
  "august",
  "september",
  "october",
  "november",
  "december",
] as const;

export type MonthSlug = (typeof MONTH_SLUGS)[number];

export const HISTORY_PAGE_SIZE = 50;

export function monthSlug(month: number): MonthSlug {
  return MONTH_SLUGS[Math.min(12, Math.max(1, month)) - 1]!;
}

export function monthFromSlug(slug: string): number | null {
  const i = MONTH_SLUGS.indexOf(slug.toLowerCase() as MonthSlug);
  return i === -1 ? null : i + 1;
}

/** Localized full month name, e.g. "August" / "Agosto" / "أغسطس". */
export function monthName(month: number, locale: Locale): string {
  const date = new Date(Date.UTC(2025, Math.max(0, month - 1), 1));
  return new Intl.DateTimeFormat(LOCALE_META[locale].intl, {
    month: "long",
    timeZone: "UTC",
  }).format(date);
}

/** "August 2026" in the active locale. */
export function monthYearLabel(month: number, year: number, locale: Locale): string {
  const date = new Date(Date.UTC(year, Math.max(0, month - 1), 1));
  return new Intl.DateTimeFormat(LOCALE_META[locale].intl, {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(date);
}

export function historyMonthPath(year: number, month: number): string {
  return `/history/${year}/${monthSlug(month)}`;
}

export function historyBoardPath(year: number, month: number, board: string): string {
  return `${historyMonthPath(year, month)}/${board}`;
}

/** Previous / next calendar month, used for prev-next internal linking. */
export function stepMonth(year: number, month: number, delta: number) {
  const index = year * 12 + (month - 1) + delta;
  return { year: Math.floor(index / 12), month: (index % 12) + 1 };
}

export interface HistoryPeriod {
  id: string;
  year: number;
  month: number;
  status: string;
  board_count: number;
  entry_count: number;
  total_heat_cents: number;
  finalized_at: string;
}

export interface HistorySnapshot {
  id: string;
  period_id: string;
  year: number;
  month: number;
  category_id: string | null;
  category_slug: string;
  category_name: string;
  category_description: string | null;
  parent_slug: string | null;
  parent_name: string | null;
  entry_count: number;
  total_heat_cents: number;
  is_public: boolean;
  hidden_reason?: string | null;
  finalized_at: string;
}

export interface HistoryEntry {
  id: string;
  snapshot_id: string;
  year: number;
  month: number;
  category_slug: string;
  category_name: string;
  position: number;
  listing_id: string | null;
  listing_name: string;
  listing_slug: string;
  listing_headline: string | null;
  listing_description: string | null;
  listing_path: string;
  external_url: string | null;
  image_url: string | null;
  owner_user_id: string | null;
  owner_slug: string | null;
  owner_name: string | null;
  owner_path: string | null;
  heat_cents: number;
  booster_count: number;
  click_count: number;
  country_code: string | null;
  badges: string[];
}

/** Achievement label key for a recorded position ("#1", "Top 3", "Top 10"). */
export function achievementKey(position: number): "one" | "top3" | "top10" | null {
  if (position === 1) return "one";
  if (position <= 3) return "top3";
  if (position <= 10) return "top10";
  return null;
}
